import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getCompliantCallTargetConfigApi,
  updateCompliantCallTargetConfigApi,
} from '@/lib/reports-api';
import {
  canSaveCompliantCallTarget,
  compliantCallTargetRangeHint,
  COMPLIANT_CALL_TARGET_LABEL,
  DEFAULT_COMPLIANT_CALL_TARGET_RANGE,
  isCompliantCallTargetInRange,
  type CompliantCallTargetRange,
} from '@/lib/compliant-call-target';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #263 — 管理员「系统设置 → KPI考核 → 合规通话目标」。
 *
 * 真实 GET/PATCH /system/compliant-call-target-config（GET 任意已登录可读，PATCH 仅 ADMIN+，
 * 按 orgId 隔离）。该目标（默认 5，区间 1-1000）即移动端首页「今天目标：N 个合规通话」的目标数，
 * 保存后运行时即时生效、写审计日志，无需重启服务。0 / 1001 / 非整数由后端返回 400。
 *
 * 渲染外的「能否保存 / 区间校验 / 文案」逻辑下沉到纯模块 @/lib/compliant-call-target，
 * 本组件仅薄包装；绝不前端 state 模拟，数据全部来自真实接口。
 */
const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

export function CompliantCallTargetSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'compliant-call-target-config'],
    queryFn: getCompliantCallTargetConfigApi,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(String(query.data.target));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (target: number) => updateCompliantCallTargetConfigApi(target),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(String(data.target));
      await queryClient.invalidateQueries({
        queryKey: ['system', 'compliant-call-target-config'],
      });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title={COMPLIANT_CALL_TARGET_LABEL}>
        <LoadingState label="正在加载合规通话目标…" />
      </Card>
    );
  }
  if (query.isError || draft === null || !query.data) {
    return (
      <Card title={COMPLIANT_CALL_TARGET_LABEL}>
        <ErrorState
          title="加载合规通话目标失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const range: CompliantCallTargetRange = {
    min: query.data.min ?? DEFAULT_COMPLIANT_CALL_TARGET_RANGE.min,
    max: query.data.max ?? DEFAULT_COMPLIANT_CALL_TARGET_RANGE.max,
    defaultTarget:
      query.data.defaultTarget ?? DEFAULT_COMPLIANT_CALL_TARGET_RANGE.defaultTarget,
  };
  const localInvalid = !isCompliantCallTargetInRange(draft, range);
  const canSave = canSaveCompliantCallTarget({
    canEdit,
    pending: mutation.isPending,
    raw: draft,
    range,
  });

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    // 只读 / 越界 / 保存中一律不触发真实 PATCH（与 canSaveCompliantCallTarget 同源）。
    if (!canSave) return;
    mutation.mutate(Number(draft.trim()));
  };

  return (
    <Card
      title={COMPLIANT_CALL_TARGET_LABEL}
      description="员工每日「合规通话」目标数。移动端首页「今天目标：N 个合规通话」据此渲染，保存后运行时即时生效、写审计日志（无需重启）。默认 5，区间 1-1000。"
    >
      <dl className="mb-4 grid grid-cols-2 gap-y-2 text-sm text-slate-700 sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">当前目标</dt>
          <dd className="mt-1 font-medium text-slate-900" data-testid="compliant-call-target-current">
            {query.data.target} 个
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">允许范围</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {range.min}-{range.max} 个
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">出厂默认</dt>
          <dd className="mt-1 font-medium text-slate-900">{range.defaultTarget} 个</dd>
        </div>
      </dl>

      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-600">
          {COMPLIANT_CALL_TARGET_LABEL}
          <span className="ml-1 text-xs text-slate-400">（{compliantCallTargetRangeHint(range)}）</span>
        </span>
        <input
          className={INPUT}
          type="number"
          inputMode="numeric"
          min={range.min}
          max={range.max}
          step={1}
          value={draft}
          disabled={!canEdit}
          onChange={(e) => {
            setSaved(false);
            setDraft(e.target.value);
          }}
          data-testid="compliant-call-target-input"
        />
      </label>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          请输入 {range.min}-{range.max} 之间的整数（0 / {range.max + 1} / 小数将被拒绝）。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={onSave}
            disabled={!canSave}
            data-testid="compliant-call-target-save"
          >
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? (
            <span className="text-xs text-emerald-600">已保存，APP 首页刷新即生效。</span>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改合规通话目标。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}
